// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which starting points this person has used, kept on the device. With the
// "icons after use" label setting a used starter shrinks to an icon with a tooltip;
// people who never use one keep the words. Also dedupes `starter.shown` to once per
// page view.

import Activity from '@app/features/konvo_activity/state/Activity';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import type {Starter} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import {makeAutoObservable} from 'mobx';

const STORAGE_KEY = 'konvo.starters.used';

function readUsed(): Record<string, number> {
	try {
		const raw = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
	} catch {
		return {};
	}
}

class StarterUsageStore {
	used: Record<string, number> = readUsed();
	/** Use from earlier sessions: a chip never shrinks under the pointer right after its first click. */
	private readonly usedBefore: Record<string, number> = {...this.used};
	private shown = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'shown' | 'usedBefore'>(this, {shown: false, usedBefore: false}, {autoBind: true});
	}

	hasUsed(starter: Starter): boolean {
		return (this.usedBefore[starter] ?? 0) > 0;
	}

	markUsed(starter: Starter): void {
		this.used = {...this.used, [starter]: (this.used[starter] ?? 0) + 1};
		try {
			getProtectedLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(this.used));
		} catch {
			// Private mode: labels just stay.
		}
	}

	/** `starter.shown` once per page view: `view` names what is on screen (a mail id, a page). */
	reportShown(starters: ReadonlyArray<Starter>, page: string, view: string): void {
		for (const starter of starters) {
			const key = `${page}:${view}:${starter}`;
			if (this.shown.has(key)) continue;
			this.shown.add(key);
			Activity.track('starter.shown', {starter, page});
		}
	}

	/** A new page view (the page was left): the same starters count as shown again. */
	resetShown(page: string): void {
		for (const key of [...this.shown]) if (key.startsWith(`${page}:`)) this.shown.delete(key);
	}
}

export default new StarterUsageStore();
